<template>
  <div>
   <h2 class="table-title">{{ $t('completed') }}</h2>
    <div v-if="loading">{{ $t('loading') }}</div>

    <div v-else class="table-wrapper">
    <table class="job-table">
      <thead>
        <tr>
          <th @click="sortBy('id')">{{ $t('jobCode') }}</th>
          <th @click="sortBy('file_name')">{{ $t('file') }}</th>
          <th>{{ $t('desc') }}</th>
          <th @click="sortBy('delivery_date')">{{ $t('deliveryDate') }}</th>
          <th>{{ $t('outputs') }}</th>
          <th>{{ $t('uploadTime') }}</th>
          <th @click="sortBy('user_name')">{{ $t('customer') }}</th>
          <th @click="sortBy('specialization')">{{ $t('specialization') }}</th>
        </tr>
      </thead>
          <tbody>
      <template v-for="job in paginatedJobs" :key="job.id">
        <tr @click="toggleDetails(job.id)" style="cursor: pointer;">
          <td>{{ formatJobCode(job.id) }}</td>
          <td>
            <a :href="`${API_URL}/uploads/${encodeURIComponent(job.file_name)}`" target="_blank">
              {{ extractOriginalFileName(job.file_name) }}
            </a>
          </td>
          <td>{{ job.description || '-' }}</td>
          <td>{{ formatDate(job.delivery_date) }}</td>
          <td>
            <span v-if="job.output_files?.length">
              <span
                v-for="(file, index) in job.output_files"
                :key="file"
                style="display: inline-block; margin-right: 6px;"
              >
                <a :href="`${API_URL}/uploads/output/${encodeURIComponent(file)}`" target="_blank">
                  {{ extractOriginalFileName(file) }}
                </a>
                <span v-if="index !== job.output_files.length - 1">,</span>
              </span>
            </span>
            <span v-else>{{ t('noOutputYet') }}</span>
          </td>
          <td>{{ formatDate(job.output_upload_date) }}</td>
          <td>{{ job.user_name }} {{ job.user_surname }}</td>
          <td>{{ job.specialization || '-' }}</td>
        </tr>

        <!-- ✔ Detay satırı -->
        <tr v-if="expandedJobId === job.id">
          <td colspan="8">
            <div class="job-details">
              <p><strong>{{ t('jobCode') }}:</strong> {{ formatJobCode(job.id) }}</p>
              <p><strong>{{ t('file') }}:</strong> {{ extractOriginalFileName(job.file_name) }}</p>
              <p><strong>{{ t('desc') }}:</strong> {{ job.description }}</p>
              <p><strong>{{ t('deliveryDate') }}:</strong> {{ formatDate(job.delivery_date) }}</p>
              <p><strong>{{ t('outputs') }}:</strong>
                <ul v-if="job.output_files?.length">
                  <li v-for="file in job.output_files" :key="file">
                    <a :href="`${API_URL}/uploads/output/${encodeURIComponent(file)}`" target="_blank">
                      {{ extractOriginalFileName(file) }}
                    </a>
                  </li>
                </ul>
                <span v-else>{{ t('noOutputYet') }}</span>
              </p>
              <p><strong>{{ t('uploadTime') }}:</strong> {{ formatDate(job.output_upload_date) }}</p>
              <p><strong>{{ t('customer') }}:</strong> {{ job.user_name }} {{ job.user_surname }}</p>
              <p><strong>{{ t('specialization') }}:</strong> {{ job.specialization }}</p>
            </div>
          </td>
        </tr>
      </template>
    </tbody>
    </table>
    </div>
          <!-- Mobil Kart Görünüm -->
      <div class="mobile-job-cards">
        <div
          class="mobile-card"
          v-for="job in paginatedJobs"
          :key="'card-' + job.id"
        >
          <p><strong>{{ t('jobCode') }}:</strong> {{ formatJobCode(job.id) }}</p>

          <p><strong>{{ t('file') }}:</strong>
            <a
              :href="`${API_URL}/uploads/${encodeURIComponent(job.file_name)}`"
              target="_blank"
            >
              {{ extractOriginalFileName(job.file_name) }}
            </a>
          </p>

          <p><strong>{{ t('desc') }}:</strong> {{ job.description || '-' }}</p>
          <p><strong>{{ t('deliveryDate') }}:</strong> {{ formatDate(job.delivery_date) }}</p>

          <p><strong>{{ t('outputs') }}:</strong>
            <ul v-if="job.output_files && job.output_files.length">
              <li v-for="file in job.output_files" :key="file">
                <a
                  :href="`${API_URL}/uploads/output/${encodeURIComponent(file)}`"
                  target="_blank"
                >
                  {{ extractOriginalFileName(file) }}
                </a>
              </li>
            </ul>
            <span v-else>{{ t('noOutputYet') }}</span>
          </p>

          <p><strong>{{ t('uploadTime') }}:</strong> {{ formatDate(job.output_upload_date) || '-' }}</p>
          <p><strong>{{ t('customer') }}:</strong> {{ job.user_name }} {{ job.user_surname }}</p>
          <p><strong>{{ t('specialization') }}:</strong> {{ job.specialization || '-' }}</p>
        </div>
      </div>

    <!-- Sayfalama -->
    <div class="pagination">
      <button @click="prevPage" :disabled="page === 1">‹</button>
      <span>{{ $t('page') }} {{ page }} / {{ totalPages }}</span>
      <button @click="nextPage" :disabled="page === totalPages">›</button>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, computed } from 'vue'
import { getToken } from '@/utils/auth'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const API_URL = process.env.VUE_APP_API_URL
const jobs = ref([])
const loading = ref(false)
const page = ref(1)
const perPage = 10
const sortKey = ref('specialization')
const sortDirection = ref(1)
const expandedJobId = ref(null)

function toggleDetails(id) {
  expandedJobId.value = expandedJobId.value === id ? null : id
}
function formatJobCode(id) {
  return 'JOB-' + String(id).padStart(6, '0')
}

function formatDate(date) {
  if (!date) return '-'
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}.${month}.${year}`
}

function extractOriginalFileName(filename) {
  if (!filename) return '-'
  return filename.substring(filename.indexOf('-') + 1)
}

function sortBy(key) {
  if (sortKey.value === key) {
    sortDirection.value *= -1
  } else {
    sortKey.value = key
    sortDirection.value = 1
  }
}

const sortedJobs = computed(() => {
  return [...jobs.value].sort((a, b) => {
    const valA = typeof a[sortKey.value] === 'string' ? a[sortKey.value] : String(a[sortKey.value] || '')
    const valB = typeof b[sortKey.value] === 'string' ? b[sortKey.value] : String(b[sortKey.value] || '')
    return valA.localeCompare(valB, 'tr') * sortDirection.value
  })
})

const paginatedJobs = computed(() => {
  const start = (page.value - 1) * perPage
  return sortedJobs.value.slice(start, start + perPage)
})

const totalPages = computed(() => Math.ceil(jobs.value.length / perPage))

function nextPage() {
  if (page.value < totalPages.value) page.value++
}

function prevPage() {
  if (page.value > 1) page.value--
}

async function fetchCompletedJobs() {
  loading.value = true
  const res = await fetch(`${API_URL}/translator/completed-jobs`, {
    headers: { Authorization: `Bearer ${getToken()}` }
  })
  jobs.value = await res.json()
  jobs.value.forEach(job => {
    if (!job.specialization) job.specialization = ''
  })
  loading.value = false
}

onMounted(() => {
  fetchCompletedJobs()
})
</script>

<style scoped>
.table-title {
  text-align: center;
  font-weight: bold;
  font-size: 1.6rem;
  margin-bottom: 20px;
  margin-top: 20px;
  color: #2c3e50;
}
.mobile-job-cards {
  display: none; /* Mobil kartlar GİZLİ */
}
.job-details {
  background-color: #f9f9f9;
  padding: 12px;
  border-radius: 8px;
  margin-top: 10px;
  color: #333;
  font-size: 14px;
}
.job-details p {
  margin: 4px 0;
}
.job-table {
  width: 100%;
  border-collapse: collapse;
}

.job-table th, .job-table td {
  padding: 10px;
  border-bottom: 1px solid #ccc;
}

.job-table th {
  background-color: #f7f7f7;
  cursor: pointer;
  user-select: none;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 12px;
}

.pagination button {
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  background-color: #3498db;
  color: white;
  cursor: pointer;
  transition: background 0.3s;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.table-wrapper table {
  min-width: 1000px;
}
/* Mobil Kartlar */
@media (max-width: 767px) {
  .table-wrapper {
    display: none;
  }

  .mobile-job-cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 10px;
  }

  .mobile-card {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
    padding: 14px;
    font-size: 14px;
  }

  .mobile-card p {
    margin: 6px 0;
    line-height: 1.4;
  }

  .mobile-card ul {
    padding-left: 20px;
    margin: 6px 0;
  }

  .mobile-card a {
    color: #3498db;
    text-decoration: underline;
    word-break: break-all;
  }
}

</style>
